import React, { useEffect, useRef, useState } from 'react';
import { Search } from 'react-bootstrap-icons';
import { useNavigate, useParams } from 'react-router-dom';

import { Input, Typography, Skeleton, List } from 'antd';

import ProgressStatus from '@/components/shared/ProgressStatus';
import { useGetModulesQuery } from '@/services/course';
import { useGetLessonsQuery } from '@/services/lesson';
import styled, { StyledProp } from '@/styled';

import ModuleTimeline from './ModuleTimeline';

function Timeline({ className }: StyledProp) {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { hash, id } = useParams();

  const listRef = useRef<HTMLDivElement>();

  const { data: modules, isFetching } = useGetModulesQuery({ courseHash: hash });
  const { data: lesson } = useGetLessonsQuery(id);
  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    if (listRef.current && !isFetching) {
      const currentModuleElement = listRef.current.querySelector<HTMLDivElement>('.currentModule');

      const moduleTop = currentModuleElement?.offsetTop ?? 0;
      const moduleScrollHeight = currentModuleElement?.scrollHeight ?? 0;

      const timeout = setTimeout(() => {
        listRef.current?.scrollTo(0, moduleTop - moduleScrollHeight + 10);
        clearTimeout(timeout);
      });
    }
  }, [isFetching]);

  const handleClickItemSearch = (lessonId: number, courseHash: string) => {
    navigate(`/curso/${courseHash}/aula/${lessonId}`);
  };

  if (isFetching) {
    return (
      <div className={className}>
        <Skeleton className='skeleton' />
        <Skeleton.Button size='large' className='skeleton timelineSkeleton' />
      </div>
    );
  }

  return (
    <div className={className}>
      <Typography.Title level={3} ellipsis>
        {lesson?.course_title}
      </Typography.Title>
      <ProgressStatus className='progress' />
      <Input
        size='large'
        onChange={handleChangeSearch}
        placeholder='Procurar Aula'
        prefix={<Search style={{ marginRight: 8 }} size={12} />}
      />
      {!search && (
        <div ref={listRef} className='timeline'>
          {(modules?.data ?? []).map(item => (
            <ModuleTimeline key={item.id} module={item} />
          ))}
        </div>
      )}
      {search && (
        <List className='searchList'>
          {(modules?.data ?? []).map((module, index) => {
            return module.lessons
              .filter(lesson => lesson.title.toLowerCase().includes(search.toLowerCase()))
              .map(item => (
                <Typography.Link
                  onClick={() => handleClickItemSearch(item.id, item.hash_course)}
                  className='searchItem'
                  key={`${item.title}-${index}`}
                  ellipsis
                >
                  {item.title}
                </Typography.Link>
              ));
          })}
        </List>
      )}
    </div>
  );
}

export default styled(Timeline)`
  width: 100%;

  .progress {
    margin-bottom: ${({ theme }) => theme.space.spacing(6)};
  }

  > span {
    margin-bottom: ${({ theme }) => theme.space.spacing(7)};
  }

  .timelineSkeleton {
    width: 100%;

    .ant-skeleton-button {
      width: 100%;
    }
  }

  .searchItem {
    color: ${({ theme }) => theme.colorTextSecondary};
    display: block;
  }

  .timeline {
    padding: 20px 10px;
    overflow-y: scroll;
    overflow-x: hidden;
    max-height: 720px;
  }

  .searchList {
    overflow-y: scroll;
    max-height: 720px;
    a.ant-typography {
      font-size: ${({ theme }) => theme.space.pxToRem(16)};
    }
  }
`;
